/**
 * The exporter registry: for each screen, what its Export button writes.
 *
 * Every list used to wire its own button in Header, its own window event and
 * its own Blob/download handler (32 download sites). Now a screen has ONE
 * entry in `exporters.ts` (file name + columns) and hands its current rows to
 * `useExportSource`; Header renders the same labelled Export button for all of
 * them. Adding export to a screen is one registry entry plus one hook call.
 */
import { useEffect, useRef } from 'react';
import { toCsv } from '../../utils/csv';
import { downloadText } from '../../utils/download';

export interface ExportColumn<Row, Ctx> {
  header: string;
  value: (row: Row, ctx: Ctx) => unknown;
  /** Leaves the whole column out (header included) when it returns false, e.g. money for a role that may not see it. */
  when?: (ctx: Ctx) => boolean;
}

export interface ExporterDef<Row, Ctx = undefined> {
  /** File name, or a function of the page context when it depends on a tab. */
  fileName: string | ((ctx: Ctx) => string);
  columns: ExportColumn<Row, Ctx>[];
}

export const defineExporter = <Row, Ctx = undefined>(def: ExporterDef<Row, Ctx>): ExporterDef<Row, Ctx> => def;

/** What a page hands over: the rows it is showing, plus anything its columns look up. */
export interface ExportSource<Row, Ctx> {
  rows: Row[];
  ctx: Ctx;
}

/** Build the file for one exporter from a page's current rows. Pure. */
export function buildExport<Row, Ctx>(
  def: ExporterDef<Row, Ctx>,
  source: ExportSource<Row, Ctx>,
): { fileName: string; csv: string; rowCount: number } {
  const fileName = typeof def.fileName === 'function' ? def.fileName(source.ctx) : def.fileName;
  const columns = def.columns.filter(c => !c.when || c.when(source.ctx));
  const csv = toCsv(
    columns.map(c => c.header),
    source.rows.map(row => columns.map(c => c.value(row, source.ctx))),
  );
  return { fileName, csv, rowCount: source.rows.length };
}

// ── live sources ─────────────────────────────────────────────────────────────
// A mounted screen registers a getter; the Export button asks for it on click,
// so the file always matches what the screen shows right now.

const sources = new Map<string, () => ExportSource<any, any>>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

export function registerExportSource(key: string, get: () => ExportSource<any, any>): () => void {
  sources.set(key, get);
  notify();
  return () => {
    // Only remove our own registration, not a newer one for the same key.
    if (sources.get(key) === get) sources.delete(key);
    notify();
  };
}

export const hasExportSource = (key: string) => sources.has(key);
export const subscribeExportSources = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export type ExportOutcome = { ok: true; fileName: string; rowCount: number } | { ok: false; reason: 'no-screen' | 'empty' };

/** Download the registered screen's rows as CSV. */
export function runExport(
  key: string,
  lookup: (key: string) => ExporterDef<any, any> | undefined,
): ExportOutcome {
  const def = lookup(key);
  const get = sources.get(key);
  if (!def || !get) return { ok: false, reason: 'no-screen' };
  const built = buildExport(def, get());
  if (built.rowCount === 0) return { ok: false, reason: 'empty' };
  downloadText(built.fileName, built.csv);
  return { ok: true, fileName: built.fileName, rowCount: built.rowCount };
}

/** Register this screen's current rows for its Export button. */
export function useExportRegistration(key: string, source: ExportSource<any, any>): void {
  const latest = useRef(source);
  latest.current = source;
  useEffect(() => registerExportSource(key, () => latest.current), [key]);
}
